import React from 'react';
import { motion } from 'framer-motion';
import { useSEO } from '../hooks/useSEO';

const sections = [
  {
    heading: 'Agreement to Terms',
    body: [
      'These Terms of Service ("Terms") govern your access to and use of the website and services provided by Hei Web Creations Pte Ltd ("we", "us", or "our"). By accessing our website or engaging our services, you agree to be bound by these Terms.',
    ],
  },
  {
    heading: 'Services',
    body: [
      'We provide web design, WordPress and WooCommerce development, portal development, AI-assisted website creation, website revamp, and maintenance and support services. The specific scope, deliverables, and fees for each engagement will be set out in a separate proposal or agreement.',
    ],
  },
  {
    heading: 'Quotations & Payment',
    body: [
      'All quotations are valid for the period stated within them. Fees, payment schedules, and milestones will be agreed in writing before work commences. Unless otherwise stated, a deposit may be required to begin a project, with the balance payable upon completion or per the agreed schedule.',
    ],
  },
  {
    heading: 'Client Responsibilities',
    body: [
      'You agree to provide accurate information, content, and materials required to complete your project in a timely manner. Delays in providing required materials or feedback may affect project timelines. You warrant that any content you supply does not infringe the rights of third parties.',
    ],
  },
  {
    heading: 'Intellectual Property',
    body: [
      'Upon full payment, ownership of the final delivered website and custom assets created specifically for you transfers to you, unless otherwise agreed. We retain the right to use non-confidential aspects of the work in our portfolio. Third-party assets, plugins, and licences remain subject to their respective terms.',
    ],
  },
  {
    heading: 'Revisions & Approvals',
    body: [
      'The number of revision rounds included will be specified in your proposal. Additional revisions beyond the agreed scope may incur further charges. Work is considered approved once you provide written sign-off or upon launch.',
    ],
  },
  {
    heading: 'Limitation of Liability',
    body: [
      'To the fullest extent permitted by law, we shall not be liable for any indirect, incidental, or consequential damages arising from the use of our services. Our total liability for any claim shall not exceed the fees paid for the specific engagement giving rise to the claim.',
    ],
  },
  {
    heading: 'Governing Law',
    body: [
      'These Terms are governed by and construed in accordance with the laws of Singapore. Any disputes arising shall be subject to the exclusive jurisdiction of the courts of Singapore.',
    ],
  },
  {
    heading: 'Contact Us',
    body: [
      'For any questions regarding these Terms, please contact us at hello@heiwebcreations.com or +65 9888 3331.',
    ],
  },
];

const TermsOfService = () => {
  useSEO({
    title: 'Terms of Service | Hei Web Creations Singapore',
    description:
      'Read the Terms of Service for Hei Web Creations Pte Ltd — the terms governing our web design and development services in Singapore.',
  });

  return (
    <div className="pt-32 bg-white" data-testid="terms-of-service-page">
      <section className="py-16 border-b border-gray-100">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="font-mono text-sm text-[#C8102E]">~/legal</span>
              <h1 className="text-5xl md:text-6xl font-bold text-[#1A1A1A] mt-4 mb-4 tracking-tight">
                Terms of Service
              </h1>
              <p className="font-mono text-sm text-gray-400">Last updated: January 2026</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.03 }}
                viewport={{ once: true }}
                className="mb-12"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-4 tracking-tight">
                  {section.heading}
                </h2>
                {section.body.map((para, i) => (
                  <p key={i} className="text-lg text-gray-600 leading-[1.8] mb-4">
                    {para}
                  </p>
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
